'use client'

import { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { UserModal } from '@/features/profiles'
import { RankingCard } from '@/features/ranking/components/RankingCard'
import { RankingCardSkeleton } from '@/features/ranking/components/RankingCardSkeleton'

import { useCampaignRanking } from '../hooks/useCampaignRanking'
import type { Campaign, CampaignRankingScope } from '../types/campaign'
import {
  CAMPAIGN_PAGE_VARIANT,
  deriveCampaignStatus,
  formatCampaignPeriod,
  getCampaignStatusLabel,
  getDaysRemainingLabel
} from '../lib/campaign-ui'

import { useModalContext } from '@/shared/contexts/Modal'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { api } from '@/shared/services/api'
import type { RankingUser } from '@/shared/types/user'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { NotFoundAlert } from '@/shared/ui/NotFoundAlert'
import { Page } from '@/shared/ui/Page'
import { Pagination } from '@/shared/ui/Pagination'
import { Tabs, Tab } from '@/shared/ui/Tabs'

import { CampaignContentTile } from './CampaignContentTile'
import { CampaignListSkeleton } from './CampaignListSkeleton'
import { CampaignUserRankBanner } from './CampaignUserRankBanner'

type CampaignSection = 'overview' | 'challenges' | 'ranking'

interface CampaignDetailPageProps {
  campaignId: number
}

function toRankingUser(row: {
  position: number
  user_id: number
  points: number
  is_current_user?: boolean
  user: {
    uid: string
    name: string
    avatar?: unknown
    frame?: unknown
    background?: unknown
  }
}): RankingUser {
  return {
    ...row,
    coins: 0,
    is_current_user: row.is_current_user ?? false,
    user: {
      // `uid` vem do payload sanitizado da api (UserPublicSerializer) — antes
      // era derivado de `user_id`, o que abria o modal de perfil errado.
      uid: row.user.uid,
      name: row.user.name,
      avatar: (row.user.avatar as RankingUser['user']['avatar']) ?? null,
      frame: (row.user.frame as RankingUser['user']['frame']) ?? null,
      background: (row.user.background as RankingUser['user']['background']) ?? null,
      points: row.points,
      is_current_user: row.is_current_user ?? false
    }
  }
}

export function CampaignDetailPage({ campaignId }: CampaignDetailPageProps) {
  const [section, setSection] = useState<CampaignSection>('overview')
  const [rankingScope, setRankingScope] =
    useState<CampaignRankingScope>('global')
  const [rankingPage, setRankingPage] = useState(1)
  const { openModal } = useModalContext()
  const { campaignParticipants, campaignLinks } = useUserProgress()

  // Link pelo qual o usuário entrou nesta campanha — usado para rotular a aba
  // do ranking por link com o nome do próprio link.
  const myCampaignLink = useMemo(() => {
    const participant = campaignParticipants.find(
      p => p.campaign_id === campaignId
    )
    if (!participant) return null
    return (
      campaignLinks.find(link => link.id === participant.campaign_link_id) ??
      null
    )
  }, [campaignParticipants, campaignLinks, campaignId])

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['/user/campaigns', campaignId],
    queryFn: async () => {
      const { data } = await api.get<{ record: Campaign }>(
        `/user/campaigns/${campaignId}`
      )
      return data.record
    }
  })

  const { currentUser: overviewCurrentUser } = useCampaignRanking(
    campaignId,
    'global',
    { enabled: section === 'overview' }
  )

  const {
    records: rankingRecords,
    currentUser: rankingCurrentUser,
    campaignLink: rankingCampaignLink,
    pagination: rankingPagination,
    isLoading: isRankingLoading
  } = useCampaignRanking(campaignId, rankingScope, {
    page: rankingPage,
    enabled: section === 'ranking'
  })

  const handleScopeChange = (scope: CampaignRankingScope) => {
    setRankingScope(scope)
    setRankingPage(1)
  }

  const handleShowUserProfile = (rankingUser: RankingUser) => {
    openModal({ modal: UserModal }, { userUid: rankingUser.user.uid })
  }

  if (isLoading || !campaign) {
    return (
      <Page variant={CAMPAIGN_PAGE_VARIANT}>
        <div className="flex w-full max-w-2xl flex-col px-2 sm:px-0">
          <Card variant={CAMPAIGN_PAGE_VARIANT} showTrophies className="w-full">
            <CardBody>
              <CampaignListSkeleton />
            </CardBody>
          </Card>
        </div>
      </Page>
    )
  }

  const contents = campaign.campaign_contents ?? []
  const status = deriveCampaignStatus(campaign)
  // Encerrada a campanha, restam listagem e ranking. O conteúdo sai da tela porque
  // já saiu do alcance: as unlock rules de campanha passaram a exigir período ativo,
  // e Campaigns::EndedContentGuard recusa a conclusão no backend.
  const isEnded = status === 'ended'
  // A aba Desafios some para campanha encerrada; se o aluno já estava nela quando a
  // campanha virou, cai para a visão geral em vez de ficar com o corpo vazio.
  const activeSection: CampaignSection =
    isEnded && section === 'challenges' ? 'overview' : section
  const statusLabel = getCampaignStatusLabel(status)
  const timeLabel = getDaysRemainingLabel(campaign.ends_at, status)
  const journeyCount = contents.filter(c => c.content_type === 'Journey').length
  const missionCount = contents.filter(c => c.content_type === 'Mission').length

  return (
    <Page variant={CAMPAIGN_PAGE_VARIANT}>
      <div className="flex w-full min-w-0 max-w-full flex-col items-center px-2 sm:px-0">
        <Card
          variant={CAMPAIGN_PAGE_VARIANT}
          showTrophies
          className="w-full max-w-2xl"
        >
          <CardHeader>
            <CardTitle>{campaign.name}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <Tabs
              value={activeSection}
              onValueChange={value => setSection(value as CampaignSection)}
              className="justify-center"
              visibleCountMobile={3}
            >
              <Tab value="overview">Campanha</Tab>
              {!isEnded ? <Tab value="challenges">Desafios</Tab> : null}
              <Tab value="ranking">Ranking</Tab>
            </Tabs>

            {activeSection === 'overview' ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span className="rounded-full bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#974319] shadow-[0_2px_0_0_#f39d72]">
                    {statusLabel}
                  </span>
                  <span className="rounded-full bg-white/60 px-3 py-1 text-xs font-semibold text-[#7F7FAD]">
                    {formatCampaignPeriod(campaign.starts_at, campaign.ends_at)}
                  </span>
                </div>

                <p className="text-center text-sm font-semibold text-[#c75300]">
                  {timeLabel}
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-white/50 px-3 py-3 text-center shadow-[0_3px_0_0_#f39d72]">
                    <p className="text-2xl font-bold text-[#00298F]">
                      {journeyCount}
                    </p>
                    <p className="text-xs font-semibold text-[#7F7FAD]">
                      Jornada{journeyCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/50 px-3 py-3 text-center shadow-[0_3px_0_0_#f39d72]">
                    <p className="text-2xl font-bold text-[#00298F]">
                      {missionCount}
                    </p>
                    <p className="text-xs font-semibold text-[#7F7FAD]">
                      Missão{missionCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>

                {overviewCurrentUser ? (
                  <CampaignUserRankBanner
                    currentUser={overviewCurrentUser}
                    scope="global"
                  />
                ) : null}

                <p className="text-center text-sm text-[#7F7FAD]">
                  {isEnded
                    ? 'Esta campanha foi encerrada. O ranking abaixo é o resultado final.'
                    : 'Complete os desafios vinculados para subir no ranking desta campanha.'}
                </p>
              </div>
            ) : null}

            {activeSection === 'challenges' ? (
              contents.length === 0 ? (
                <NotFoundAlert
                  title="Sem desafios por aqui"
                  text="O gestor ainda não vinculou jornadas ou missões a esta campanha."
                />
              ) : (
                <div className="flex flex-col gap-2">
                  {contents.map(content => (
                    <CampaignContentTile
                      key={content.id}
                      content={content}
                      isCampaignEnded={isEnded}
                    />
                  ))}
                </div>
              )
            ) : null}

            {activeSection === 'ranking' ? (
              <div className="space-y-4">
                <Tabs
                  value={rankingScope}
                  onValueChange={value =>
                    handleScopeChange(value as CampaignRankingScope)
                  }
                  className="justify-center"
                  visibleCountMobile={3}
                >
                  <Tab value="global">Geral</Tab>
                  <Tab value="friends">Amigos</Tab>
                  <Tab value="link">{myCampaignLink?.label ?? 'Meu link'}</Tab>
                </Tabs>

                {rankingScope === 'link' && rankingCampaignLink ? (
                  <p className="text-center text-sm font-semibold text-[#7F7FAD]">
                    {rankingCampaignLink.participants_count} participante
                    {rankingCampaignLink.participants_count === 1
                      ? ' entrou'
                      : 's entraram'}{' '}
                    por este link
                  </p>
                ) : null}

                {rankingCurrentUser ? (
                  <CampaignUserRankBanner
                    currentUser={rankingCurrentUser}
                    scope={rankingScope}
                  />
                ) : null}

                <div className="flex flex-col gap-2">
                  {isRankingLoading ? (
                    <>
                      <RankingCardSkeleton />
                      <RankingCardSkeleton />
                      <RankingCardSkeleton />
                    </>
                  ) : rankingRecords.length === 0 ? (
                    <NotFoundAlert
                      title="Ninguém no ranking ainda"
                      text="Seja o primeiro a pontuar completando os desafios da campanha!"
                    />
                  ) : (
                    rankingRecords.map(row => (
                      <RankingCard
                        key={row.user_id}
                        rankingUser={toRankingUser(row)}
                        onClick={() =>
                          handleShowUserProfile(toRankingUser(row))
                        }
                      />
                    ))
                  )}

                  {rankingScope === 'link' &&
                  rankingPagination &&
                  rankingPagination.pages > 1 ? (
                    <Pagination
                      currentPage={rankingPagination.page}
                      pageCount={rankingPagination.pages}
                      totalCount={rankingPagination.count}
                      onPrev={() => setRankingPage(p => Math.max(1, p - 1))}
                      onNext={() => setRankingPage(p => p + 1)}
                      ariaLabel="Paginação do ranking do link"
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </Page>
  )
}
